/**
 * Manual booking creation — opens a dialog with a button. Used by Admin, eligible
 * Vendor (admin granted), and Agent (with `manual_bookings` permission).
 *
 * Form sections (all inside one scrollable Dialog) — most sub-sections live in
 * dedicated components after the Iter 56 split:
 *   1. Car + locations + dates + handover times  (inline)
 *   2. Insurance plan + add-ons                  (inline)
 *   3. Customer fields                           → `ManualBookingCustomerForm`
 *   4. Optional second driver                    → `ManualBookingSecondDriverPanel`
 *   5. Payment status + notes                    (inline)
 *   6. Live price breakdown card                 → `ManualBookingQuoteSummary`
 *
 * For vendors, `restrictToVendorId` should be the vendor's own id so they can
 * only book their own cars. For admins / agents, leave undefined to expose
 * every car.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, CalendarPlus, Shield, Link2 } from "lucide-react";
import { toast } from "sonner";
import Money from "@/components/Money";
import { ManualBookingCustomerForm } from "@/components/booking/ManualBookingCustomerForm";
import { ManualBookingSecondDriverPanel } from "@/components/booking/ManualBookingSecondDriverPanel";
import { ManualBookingPriceDetails } from "@/components/booking/ManualBookingPriceDetails";
import { ManualBookingExtrasEditor } from "@/components/booking/ManualBookingExtrasEditor";
import { emptyPricingSpec, buildPricingPayload, computeManualPricing, num } from "@/lib/manualPricing";
import { TIME_OPTIONS, initialManualBookingForm } from "@/components/booking/manualBookingDefaults";
import SendBookingDialog from "@/components/booking/SendBookingDialog";

export default function ManualBookingDialog({ restrictToVendorId = null, onCreated, triggerLabel = "Create booking" }) {
  const [open, setOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => initialManualBookingForm());
  const [secondDriver, setSecondDriver] = useState(null); // null = none, object = on
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [sendBookingId, setSendBookingId] = useState(null);
  const [extras, setExtras] = useState([]);
  const [pricingSpec, setPricingSpec] = useState(() => emptyPricingSpec());
  const vatPrefilled = useRef(false);
  const debounceRef = useRef(null);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/cars")
      .then((r) => {
        let list = r.data || [];
        if (restrictToVendorId) list = list.filter((c) => c.vendor_id === restrictToVendorId);
        setCars(list);
      })
      .catch(() => toast.error("Could not load cars"))
      .finally(() => setLoading(false));
  }, [open, restrictToVendorId]);

  const selectedCar = useMemo(() => cars.find((c) => c.id === form.car_id) || null, [cars, form.car_id]);
  const pickupOptions = useMemo(() => selectedCar?.pickup_locations || (selectedCar?.location ? [selectedCar.location] : []), [selectedCar]);
  const dropoffOptions = useMemo(() => selectedCar?.dropoff_locations || (selectedCar?.location ? [selectedCar.location] : []), [selectedCar]);
  const insurancePlans = selectedCar?.insurance_plans || [];
  const addOnsCatalog = selectedCar?.add_ons || [];

  // Reset car-dependent selections when the car changes.
  useEffect(() => {
    if (!selectedCar) return;
    setForm((f) => ({
      ...f,
      pickup_location: f.pickup_location && pickupOptions.includes(f.pickup_location) ? f.pickup_location : (pickupOptions[0] || ""),
      dropoff_location: f.dropoff_location && dropoffOptions.includes(f.dropoff_location) ? f.dropoff_location : (dropoffOptions[0] || ""),
      // Default to the first insurance plan (typically the "basic" one)
      insurance_plan_id: insurancePlans[0]?.id || null,
      // Wipe add-on selection — different car catalog
      add_on_ids: [],
    }));
    setExtras([]);
    vatPrefilled.current = false;
    // eslint-disable-next-line
  }, [selectedCar?.id]);

  // Re-fetch the live quote whenever any priced field changes.
  useEffect(() => {
    if (!form.car_id || !form.start_date || !form.end_date) { setQuote(null); return; }
    if (new Date(form.end_date) <= new Date(form.start_date)) { setQuote(null); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setQuoteLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/cars/${form.car_id}/quote`, {
          start_date: new Date(`${form.start_date}T00:00:00Z`).toISOString(),
          end_date: new Date(`${form.end_date}T00:00:00Z`).toISOString(),
          pickup_location: form.pickup_location || null,
          dropoff_location: form.dropoff_location || null,
          insurance_plan_id: form.insurance_plan_id || null,
          add_on_ids: [],
        });
        setQuote(data);
        if (!vatPrefilled.current && data.vendor_vat_rate_pct > 0) {
          vatPrefilled.current = true;
          setPricingSpec((s) => (s.vat_rate_pct === "" ? { ...s, vat_rate_pct: String(data.vendor_vat_rate_pct) } : s));
        }
      } catch (e) {
        setQuote(null);
      } finally {
        setQuoteLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.car_id, form.start_date, form.end_date, form.pickup_location, form.dropoff_location, form.insurance_plan_id]);

  const days = useMemo(() => {
    if (!form.start_date || !form.end_date) return 1;
    return Math.max(1, Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000));
  }, [form.start_date, form.end_date]);

  const priceBase = useMemo(() => ({
    days,
    rental_subtotal: quote?.subtotal ?? 0,
    route_fee: quote?.route_fee ?? 0,
    meet_greet_fee: quote?.meet_greet_fee ?? 0,
    insurance_fee: quote?.insurance_fee ?? 0,
    deposit: quote?.deposit ?? 0,
  }), [quote, days]);

  const submit = async () => {
    if (!form.car_id) { toast.error("Pick a car"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Start and end dates are required"); return; }
    if (new Date(form.end_date) <= new Date(form.start_date)) { toast.error("End date must be after start date"); return; }
    if (!form.customer_email || !form.customer_name) { toast.error("Customer name and email are required"); return; }
    if (secondDriver && (!secondDriver.full_name || !secondDriver.license_number)) {
      toast.error("Second driver name and licence number are required");
      return;
    }
    setSubmitting(true);
    try {
      // "Already collected" with no explicit amount = full grand total was paid.
      const spec = { ...pricingSpec };
      const live = computeManualPricing(priceBase, spec, extras);
      if (form.payment_status === "paid" && num(spec.amount_paid) === null) {
        spec.amount_paid = String(live.grand);
      }
      const payload = {
        car_id: form.car_id,
        start_date: new Date(`${form.start_date}T00:00:00Z`).toISOString(),
        end_date: new Date(`${form.end_date}T00:00:00Z`).toISOString(),
        pickup_location: form.pickup_location || null,
        dropoff_location: form.dropoff_location || null,
        pickup_time: form.pickup_time || null,
        return_time: form.return_time || null,
        insurance_plan_id: form.insurance_plan_id || null,
        add_on_ids: [],
        pricing: buildPricingPayload(spec, extras),
        second_driver: secondDriver || null,
        customer_email: form.customer_email.trim(),
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || null,
        customer_address: form.customer_address.trim() || null,
        customer_country: form.customer_country || null,
        customer_id_number: form.customer_id_number.trim() || null,
        customer_license_number: form.customer_license_number.trim() || null,
        customer_license_expiry: form.customer_license_expiry || null,
        payment_status: form.payment_status,
        notes: form.notes.trim() || null,
      };
      const { data } = await api.post("/bookings/manual", payload);
      toast.success(`Booking created for ${data.customer_email}`);
      setOpen(false);
      setForm(initialManualBookingForm());
      setSecondDriver(null);
      setQuote(null);
      setExtras([]);
      setPricingSpec(emptyPricingSpec());
      vatPrefilled.current = false;
      setSendBookingId(data.booking_id || null);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // Iter 114 — save the current configuration as a shareable priced quote.
  const [quoteLink, setQuoteLink] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const createQuoteLink = async () => {
    if (!form.car_id) { toast.error("Pick a car"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Start and end dates are required"); return; }
    if (new Date(form.end_date) <= new Date(form.start_date)) { toast.error("End date must be after start date"); return; }
    setQuoting(true);
    try {
      const { data } = await api.post("/vendor/quotes", {
        car_id: form.car_id,
        start_date: new Date(`${form.start_date}T00:00:00Z`).toISOString(),
        end_date: new Date(`${form.end_date}T00:00:00Z`).toISOString(),
        pickup_location: form.pickup_location || null,
        dropoff_location: form.dropoff_location || null,
        pickup_time: form.pickup_time || null,
        return_time: form.return_time || null,
        insurance_plan_id: form.insurance_plan_id || null,
        pricing: buildPricingPayload(pricingSpec, extras),
        customer_name: form.customer_name.trim() || null,
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        notes: form.notes.trim() || null,
      });
      setQuoteLink(data);
      toast.success("Quote link created");
    } catch (e) { toast.error(formatApiError(e)); } finally { setQuoting(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="manual-booking-trigger">
        <Plus className="h-4 w-4 mr-1.5" />{triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5" />Create manual booking</DialogTitle>
            <DialogDescription>Record an off-platform reservation. The full total updates live as you change options.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-5 mt-2">
            {/* LEFT — form */}
            <div className="space-y-4">
              {/* Car */}
              <div>
                <Label>Car</Label>
                <Select value={form.car_id} onValueChange={set("car_id")}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-car"><SelectValue placeholder={loading ? "Loading cars…" : "Pick a car"} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {cars.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.make} {c.model} · {c.location}{!c.is_active ? " · (paused)" : ""}
                      </SelectItem>
                    ))}
                    {cars.length === 0 && !loading && <div className="px-3 py-2 text-xs text-neutral-500">No cars available</div>}
                  </SelectContent>
                </Select>
              </div>

              {/* Locations */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Pickup</Label>
                  <Select value={form.pickup_location || ""} onValueChange={set("pickup_location")} disabled={!selectedCar}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-pickup"><SelectValue placeholder="Pickup location" /></SelectTrigger>
                    <SelectContent>{pickupOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Drop-off</Label>
                  <Select value={form.dropoff_location || ""} onValueChange={set("dropoff_location")} disabled={!selectedCar}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-dropoff"><SelectValue placeholder="Drop-off location" /></SelectTrigger>
                    <SelectContent>{dropoffOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dates + times */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => set("start_date")(e.target.value)} className="mt-1 rounded-xl" data-testid="manual-booking-start-date" />
                </div>
                <div>
                  <Label>Pickup time</Label>
                  <Select value={form.pickup_time} onValueChange={set("pickup_time")}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-pickup-time"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">{TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => set("end_date")(e.target.value)} className="mt-1 rounded-xl" data-testid="manual-booking-end-date" />
                </div>
                <div>
                  <Label>Return time</Label>
                  <Select value={form.return_time} onValueChange={set("return_time")}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-return-time"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60">{TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Insurance */}
              {insurancePlans.length > 0 && (
                <div className="rounded-xl border border-border p-3" data-testid="manual-booking-insurance-section">
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-2 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" />Insurance</div>
                  <div className="space-y-1.5">
                    {insurancePlans.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => set("insurance_plan_id")(p.id)}
                        data-testid={`manual-insurance-${p.id}`}
                        className={`w-full text-left rounded-lg border px-3 py-2 flex items-center justify-between gap-2 transition ${
                          form.insurance_plan_id === p.id ? "border-blue-500 bg-blue-50/40" : "border-border hover:bg-neutral-50"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{p.name}</div>
                          {p.description && <div className="text-xs text-neutral-500 truncate">{p.description}</div>}
                        </div>
                        <div className="text-sm text-neutral-700 tabular-nums shrink-0"><Money amount={p.price_per_day} />/day</div>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-neutral-500">Override insurance total (€):</span>
                    <Input type="number" inputMode="decimal" value={pricingSpec.insurance_price_override}
                      onChange={(e) => setPricingSpec((s) => ({ ...s, insurance_price_override: e.target.value }))}
                      placeholder="auto" className="h-8 w-28 rounded-lg text-sm" data-testid="manual-insurance-override" />
                  </div>
                </div>
              )}

              {/* Extras — qty, price override, free flag, custom extras */}
              <ManualBookingExtrasEditor catalog={addOnsCatalog} extras={extras} setExtras={setExtras} days={days} />

              <ManualBookingCustomerForm form={form} set={set} />
              <ManualBookingSecondDriverPanel secondDriver={secondDriver} setSecondDriver={setSecondDriver} />

              {/* Payment + notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Payment status</Label>
                  <Select value={form.payment_status} onValueChange={set("payment_status")}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="manual-booking-payment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Already collected (paid)</SelectItem>
                      <SelectItem value="pending">Collect at pickup (pending)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Internal notes (optional)</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={2} className="mt-1 rounded-xl" placeholder="Cash payment received, repeat customer, etc." data-testid="manual-booking-notes" />
              </div>
            </div>

            {/* RIGHT — editable price details + live summary */}
            <div className="self-start lg:sticky lg:top-2">
              {quoteLoading && <div className="text-xs text-neutral-500 mb-1.5 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Recalculating…</div>}
              {!selectedCar || !quote ? (
                <div className="rounded-2xl border border-dashed border-border p-5 text-sm text-neutral-500" data-testid="manual-booking-quote-empty">
                  Pick a car and dates to see the price details.
                </div>
              ) : (
                <ManualBookingPriceDetails
                  base={priceBase}
                  spec={pricingSpec}
                  setSpec={setPricingSpec}
                  extras={extras}
                  insuranceName={insurancePlans.find((x) => x.id === form.insurance_plan_id)?.name}
                />
              )}
            </div>
          </div>

          {quoteLink && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-3 mt-3 text-sm" data-testid="quote-link-panel">
              <div className="font-bold text-emerald-800 mb-1">Quote link ready · <Money amount={quoteLink.grand_total} /> · valid until {(quoteLink.expires_at || "").slice(0, 10)}</div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs bg-white border border-border rounded px-2 py-1 max-w-full truncate" data-testid="quote-link-url">{quoteLink.url}</code>
                <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" data-testid="quote-link-copy"
                  onClick={() => { navigator.clipboard?.writeText(quoteLink.url); toast.success("Link copied"); }}>Copy</Button>
                <a href={`https://wa.me/${(form.customer_phone || "").replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Here is your rental quote: ${quoteLink.url}`)}`}
                  target="_blank" rel="noreferrer"
                  className="text-xs font-bold text-emerald-700 underline" data-testid="quote-link-whatsapp">Send via WhatsApp</a>
              </div>
              <p className="text-[11px] text-emerald-700 mt-1">The customer can approve this quote online — it then becomes a confirmed reservation automatically.</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white border-t border-border mt-4 -mx-6 px-6 pb-1">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="manual-booking-cancel">Cancel</Button>
            <Button variant="outline" onClick={createQuoteLink} disabled={quoting || !quote} className="rounded-full" data-testid="manual-booking-quote-link">
              {quoting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
              Quote link
            </Button>
            <Button onClick={submit} disabled={submitting} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="manual-booking-submit">
              {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />}
              {quote ? <>Create booking · <Money amount={computeManualPricing(priceBase, pricingSpec, extras).grand} className="ml-1.5" /></> : "Create booking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Iter 106 — right after creating: offer Email / WhatsApp send. */}
      <SendBookingDialog bookingId={sendBookingId} open={!!sendBookingId} onClose={() => setSendBookingId(null)} />
    </>
  );
}
